'use client';

import { useCallback, useMemo, useState } from 'react';
import useCurrentWalletAdapter from 'src/hooks/useCurrentWalletAdapter';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { notify } from 'src/utils/notifications';
import { getAccountDeletionMessage } from 'src/constants/accountDeletion';

interface DeleteAccountState {
	isLoading: boolean;
	error: string | null;
}

export const useDeleteAccount = () => {
	const [deletionState, setDeletionState] = useState<DeleteAccountState>({
		isLoading: false,
		error: null,
	});

	const wallet = useCurrentWalletAdapter();
	const authority = useDriftStore((s) => s.wallet.current.adapter?.publicKey);
	const accounts = useDriftAccountsStore((s) => s.accounts);

	const hasSubaccounts = useMemo(() => {
		if (!authority) return false;

		const authorityStr = authority.toBase58();
		const userAccounts = Object.values(accounts).filter(
			(account) => account?.authority?.toBase58() === authorityStr
		);

		return userAccounts.length > 0;
	}, [authority, accounts]);

	const canDelete = useMemo(() => {
		return !!authority && !hasSubaccounts;
	}, [authority, hasSubaccounts]);

	const deleteAccount = useCallback(async () => {
		if (!authority) {
			setDeletionState({ isLoading: false, error: 'Wallet not connected' });
			return { success: false, error: 'Wallet not connected' };
		}

		if (hasSubaccounts) {
			setDeletionState({
				isLoading: false,
				error: 'Please delete all subaccounts first',
			});
			return { success: false, error: 'Please delete all subaccounts first' };
		}

		setDeletionState({ isLoading: true, error: null });

		const toastId = `delete-account-${Date.now()}`;

		try {
			notify({
				type: 'info',
				message: 'Requesting signature...',
				id: toastId,
				showUntilCancelled: true,
			});

			const timestamp = Date.now();
			const message = getAccountDeletionMessage(
				authority.toBase58(),
				timestamp
			);
			const messageBytes = new TextEncoder().encode(message);

			let signature: Uint8Array;
			try {
				signature = await wallet.signMessage(messageBytes);
			} catch (signError) {
				notify({
					type: 'error',
					message: 'Signature rejected',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
				});
				setDeletionState({ isLoading: false, error: 'Signature rejected' });
				return { success: false, error: 'Signature rejected' };
			}

			const signatureBase64 = Buffer.from(signature).toString('base64');

			notify({
				type: 'info',
				message: 'Deleting account data...',
				id: toastId,
				updatePrevious: true,
				showUntilCancelled: true,
			});

			const deleteResponse = await fetch('/api/account-deletion', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					authority: authority.toBase58(),
					signature: signatureBase64,
					message,
				}),
			});

			if (!deleteResponse.ok) {
				const deleteError = await deleteResponse.json();
				throw new Error(deleteError.message || 'Failed to delete account');
			}

			const deleteData = await deleteResponse.json();

			if (!deleteData.success) {
				throw new Error(
					deleteData.message || 'Some deletion operations failed'
				);
			}

			notify({
				type: 'success',
				message:
					'Deletion request received. Your data will be fully deleted within 30 days.',
				id: toastId,
				updatePrevious: true,
				showUntilCancelled: false,
				lengthMs: 5000,
			});

			setDeletionState({ isLoading: false, error: null });
			return { success: true };
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : 'Failed to delete account';

			notify({
				type: 'error',
				message: errorMessage,
				id: toastId,
				updatePrevious: true,
				showUntilCancelled: false,
			});

			setDeletionState({ isLoading: false, error: errorMessage });
			return { success: false, error: errorMessage };
		}
	}, [authority, wallet, hasSubaccounts]);

	return {
		deleteAccount,
		isLoading: deletionState.isLoading,
		error: deletionState.error,
		hasSubaccounts,
		canDelete,
	};
};
